import type { TargetProfileOption } from '../services/apiService';

export function buildTargetProfileMap(options: TargetProfileOption[]): Map<number, TargetProfileOption> {
  const map = new Map<number, TargetProfileOption>();

  const traverse = (nodes: TargetProfileOption[]) => {
    for (const node of nodes) {
      map.set(node.id, node);
      if (node.children && node.children.length > 0) {
        traverse(node.children);
      }
    }
  };

  traverse(options);
  return map;
}

export function collectAllDescendantIds(option: TargetProfileOption): number[] {
  const ids: number[] = [option.id];
  for (const child of option.children ?? []) {
    ids.push(...collectAllDescendantIds(child));
  }
  return ids;
}

export function computeTopLevelSelections(selectedIds: number[], map: Map<number, TargetProfileOption>): number[] {
  const selectedSet = new Set(selectedIds);
  const topLevelIds: number[] = [];

  for (const id of selectedIds) {
    const option = map.get(id);
    if (!option) continue;

    let parentId = option.parentId ?? null;
    let hasSelectedAncestor = false;
    while (parentId !== null && parentId !== undefined) {
      if (selectedSet.has(parentId)) {
        hasSelectedAncestor = true;
        break;
      }
      const parent = map.get(parentId);
      if (!parent) break;
      parentId = parent.parentId ?? null;
    }

    if (!hasSelectedAncestor) {
      topLevelIds.push(id);
    }
  }

  return Array.from(new Set(topLevelIds));
}

export function formatTargetProfilePath(option: TargetProfileOption, map: Map<number, TargetProfileOption>): string {
  const names: string[] = [];
  let current: TargetProfileOption | undefined = option;
  const visited = new Set<number>();

  while (current && !visited.has(current.id)) {
    visited.add(current.id);
    names.unshift(current.name);
    if (!current.parentId) break;
    current = map.get(current.parentId);
  }

  return names.join(' > ');
}

export function buildTargetProfileSummary(
  b2bIds: number[],
  b2cIds: number[],
  map: Map<number, TargetProfileOption>
): string {
  const formatSelection = (ids: number[]) => {
    const topLevel = computeTopLevelSelections(ids, map);
    const unique = Array.from(new Set(topLevel));
    const labels = unique
      .map(id => {
        const option = map.get(id);
        return option ? formatTargetProfilePath(option, map) : null;
      })
      .filter((value): value is string => Boolean(value));
    return labels.join(', ');
  };

  const b2bText = formatSelection(b2bIds);
  const b2cText = formatSelection(b2cIds);

  return `B2B: ${b2bText || 'None'} | B2C: ${b2cText || 'None'}`;
}
